/**
 * F11 DienstenPlein — gedeelde DTO's, Zod-schemas en label-maps.
 *
 * Concept: interne dienstencatalogus — overzicht van diensten die Streep
 * aanbiedt (onderhoud / projectinstallatie / storingsdienst / advies /
 * inspectie / etc.). Doel: standardisatie van wat Streep verkoopt,
 * makkelijker offertes maken, KPI's per dienst.
 *
 * Backoffice-only beheermodule (geen portal-flow). Permission-key DIENSTEN.
 */
import { z } from 'zod'

// ─── Enums ──────────────────────────────────────────────────────────────────

export const DIENST_STATUS = ['ACTIEF', 'PAUSE', 'GEARCHIVEERD'] as const
export type DienstStatus = (typeof DIENST_STATUS)[number]

export const DIENST_TARIFERING = [
  'UURTARIEF',
  'DAGTARIEF',
  'VAST_BEDRAG',
  'PROJECT_OFFERTE',
] as const
export type DienstTarifering = (typeof DIENST_TARIFERING)[number]

// ─── Labels ─────────────────────────────────────────────────────────────────

export const DIENST_STATUS_LABELS: Record<DienstStatus, string> = {
  ACTIEF: 'Actief',
  PAUSE: 'Gepauzeerd',
  GEARCHIVEERD: 'Gearchiveerd',
}

export const DIENST_TARIFERING_LABELS: Record<DienstTarifering, string> = {
  UURTARIEF: 'Uurtarief',
  DAGTARIEF: 'Dagtarief',
  VAST_BEDRAG: 'Vast bedrag',
  PROJECT_OFFERTE: 'Project / offerte',
}

export const DIENST_STATUS_BADGE: Record<
  DienstStatus,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'info'
> = {
  ACTIEF: 'success',
  PAUSE: 'warning',
  GEARCHIVEERD: 'secondary',
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const optionalString = z
  .string()
  .trim()
  .max(500)
  .optional()
  .nullable()
  .transform((v) => (v == null || v.trim() === '' ? null : v))

// Korte sleutel mag letters, cijfers, streepjes en underscores bevatten.
const CODE_RE = /^[A-Z0-9][A-Z0-9_-]{0,49}$/

// ─── Zod-schemas ────────────────────────────────────────────────────────────

export const createDienstSchema = z
  .object({
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(CODE_RE, 'Code mag alleen hoofdletters, cijfers, _ en - bevatten (max 50)'),
    naam: z.string().trim().min(1).max(120),
    categorie: z.string().trim().min(1).max(80),
    beschrijving: z.string().trim().min(1).max(4000),
    status: z.enum(DIENST_STATUS).optional(),
    tarifering: z.enum(DIENST_TARIFERING),

    uurtarief: z.number().min(0).max(10_000).nullable().optional(),
    dagtarief: z.number().min(0).max(100_000).nullable().optional(),
    vastBedrag: z.number().min(0).max(10_000_000).nullable().optional(),

    departmentId: z.string().min(1).nullable().optional(),
    estimatedUren: z.number().min(0).max(100_000).nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.tarifering === 'UURTARIEF' && (val.uurtarief == null || val.uurtarief <= 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['uurtarief'],
        message: 'Uurtarief is verplicht bij tarifering UURTARIEF',
      })
    }
    if (val.tarifering === 'DAGTARIEF' && (val.dagtarief == null || val.dagtarief <= 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dagtarief'],
        message: 'Dagtarief is verplicht bij tarifering DAGTARIEF',
      })
    }
    if (val.tarifering === 'VAST_BEDRAG' && (val.vastBedrag == null || val.vastBedrag <= 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['vastBedrag'],
        message: 'Vast bedrag is verplicht bij tarifering VAST_BEDRAG',
      })
    }
  })
export type CreateDienstInput = z.infer<typeof createDienstSchema>

// PATCH gebruikt een losse partial — superRefine van create wordt niet
// uitgevoerd zodat callers slechts één veld kunnen updaten zonder de
// volledige set tarifering+bedrag te hoeven hersturen.
export const updateDienstSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(CODE_RE, 'Code mag alleen hoofdletters, cijfers, _ en - bevatten (max 50)')
    .optional(),
  naam: z.string().trim().min(1).max(120).optional(),
  categorie: z.string().trim().min(1).max(80).optional(),
  beschrijving: z.string().trim().min(1).max(4000).optional(),
  status: z.enum(DIENST_STATUS).optional(),
  tarifering: z.enum(DIENST_TARIFERING).optional(),

  uurtarief: z.number().min(0).max(10_000).nullable().optional(),
  dagtarief: z.number().min(0).max(100_000).nullable().optional(),
  vastBedrag: z.number().min(0).max(10_000_000).nullable().optional(),

  departmentId: z.string().min(1).nullable().optional(),
  estimatedUren: z.number().min(0).max(100_000).nullable().optional(),
})
export type UpdateDienstInput = z.infer<typeof updateDienstSchema>

export const listDienstQuerySchema = z.object({
  status: z.enum(DIENST_STATUS).optional(),
  categorie: z.string().trim().min(1).optional(),
  departmentId: z.string().min(1).optional(),
  zoek: z.string().trim().min(1).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(2000).default(50),
})
export type ListDienstQuery = z.infer<typeof listDienstQuerySchema>

// optionalString is intentionally exported so consumers can reuse the
// trim/null normalisation when extending the schema later.
export const _optionalDienstString = optionalString

// ─── DTO's ──────────────────────────────────────────────────────────────────

export interface DienstDepartmentDTO {
  id: string
  name: string
}

export interface DienstListRowDTO {
  id: string
  code: string
  naam: string
  categorie: string
  status: DienstStatus
  tarifering: DienstTarifering

  uurtarief: number | null
  dagtarief: number | null
  vastBedrag: number | null

  estimatedUren: number | null

  department: DienstDepartmentDTO | null

  createdAt: string
  updatedAt: string
}

export interface DienstDetailDTO extends DienstListRowDTO {
  beschrijving: string
}

export interface DienstStatsDTO {
  totaal: number
  actief: number
  pause: number
  gearchiveerd: number
  categorieenAantal: number
}

/**
 * Per-dienst detail-statistieken voor het Statistieken-tab op
 * `/dienstenplein/catalogus/[id]`. Single-shot aggregator zodat de
 * tab één request hoeft te doen.
 *
 * Velden zijn intentioneel cross-data zonder eigen Order/Werkbon→Dienst
 * FK te vereisen — we gebruiken bestaande velden (department, categorie,
 * tarifering) om vergelijkbare diensten + relatieve positie te tonen.
 */
export interface DienstDetailStatsDTO {
  /** Andere diensten in dezelfde categorie (excl. deze). */
  zelfdeCategorie: number
  /** Diensten in dezelfde tarifering (excl. deze). */
  zelfdeTarifering: number
  /** Aantal werkbonnen in zelfde department als eigenaar — afgelopen jaar. */
  werkbonnenZelfdeTeamLaatsteJaar: number
  /** Totaal aantal werkbonnen in zelfde department — alle tijd. */
  werkbonnenZelfdeTeamTotaal: number
  /** Aantal actieve medewerkers in zelfde department. */
  medewerkersInTeam: number
  /** Gemiddelde estimatedUren binnen de categorie (decimaal). */
  gemEstimatedUrenInCategorie: number
  /** Aantal diensten in zelfde categorie incl. deze (basis voor positie). */
  positieInCategorie: number | null
}


// ─── Dashboard (fs-pro DienstenPlein.tsx parity) ─────────────────────────────

/**
 * KPI-tegels voor het DienstenPlein-dashboard. Komt uit fs-pro 1:1.
 *  - aantalMedewerkers      = Employee.count waar status = ACTIEF
 *  - actieveWerkbonnenAantal = Werkbon.count waar status != GEFACTUREERD/AFGEKEURD
 *                              (alle "lopende" werkbonnen)
 *  - ingeplandDezeWeekUren   = sum(WerkbonDayEntry.hours) over deze week (ma-vr)
 */
export interface DienstenpleinKpiDTO {
  aantalMedewerkers: number
  actieveWerkbonnenAantal: number
  ingeplandDezeWeekUren: number
}

/** Verlofverzoek-rij voor de inbox-tabel op het dashboard. */
export interface DienstenpleinVerlofRow {
  id: string
  employeeId: string
  employeeNaam: string
  /** Rauwe LeaveType uit Prisma (VAKANTIE / ATV / etc.) - voor sortering/badge-stijl. */
  type: string
  /**
   * Kant-en-klaar NL-classificatielabel (FO-071), server-side afgeleid via
   * `classificeerVerlof` (methode-eerst, type-terugval, bijzonderReden-verfijning).
   * De weergave gebruikt dit i.p.v. het rauwe `type` en volgt dezelfde
   * classificatie-regel als de verlofmodule (al toont het dashboard reden inline
   * waar de verlofmodule die als apart detailveld heeft).
   */
  classificatie: string
  startDate: string
  endDate: string | null
  aantalDagen: number
  status:
    | 'INGEDIEND'
    | 'GOEDGEKEURD_TEAMLEIDER'
    | 'AFGEKEURD_TEAMLEIDER'
    | 'GECLASSIFICEERD'
    | 'DEFINITIEF'
    | 'OVERRULED'
    | 'AFGEKEURD'
    | 'INGETROKKEN'
  updatedAt: string
}

export interface DienstenpleinDashboardDTO {
  kpi: DienstenpleinKpiDTO
  /** Open verlofverzoeken (INGEDIEND of GOEDGEKEURD_TEAMLEIDER). */
  verlofVerzoeken: DienstenpleinVerlofRow[]
  /** Recent afgehandelde verlofverzoeken (DEFINITIEF / AFGEKEURD / AFGEKEURD_TEAMLEIDER). Top 5. */
  recentAfgehandeld: DienstenpleinVerlofRow[]
  /** Aantal openstaande dienstwijziging-verzoeken (MutatieVerzoek.status = INGEDIEND). */
  dienstwijzigingVerzoekenAantal: number
}
